import {
  type Pagination,
  type ProgramEventSlim,
  type ProgramListEvent,
  createProgramListEvent,
  getProgramEventSlimData,
} from 'models';
import { useInfiniteQuery } from 'query';
import type { ApiClientResponseError, DefaultResponseErrorDataObject } from 'common/services';
import type { ProgramEventSlimDataObject, ProgramListEventDataObject } from 'models/service';
import { getEvents } from '../../services';
import type { ChannelsEventsListFilter, EventsListFilter } from '../../types';
import { getEventsInfiniteQueryKey } from '../keys';
import { useGetEventsParameters } from './hooks';

/** @deprecated
 * This type is deprecated.
 * It will be removed once `useEventsInfiniteQuery` response types are unified.
 * Use `EventsQueryParams` instead.
 * */
type ChannelsEventsQueryParams = {
  filter?: ChannelsEventsListFilter;
  pagination: Omit<Pagination, 'position'>;
};

// TODO: undo omit once `useEventsInfiniteQuery` response types are unified
type EventsQueryParams = {
  filter?: Omit<EventsListFilter, 'organizationId' | 'scope'>;
  options?: { enabled?: boolean };
  pagination: Omit<Pagination, 'position'>;
};

/** @deprecated
 * This useQuery is deprecated.
 * It will be removed once `useEventsInfiniteQuery` response types are unified.
 * Use `useEventsInfiniteQuery` instead.
 * */
export const useChannelsEventsInfiniteQuery = ({ filter, pagination }: ChannelsEventsQueryParams) =>
  useInfiniteQuery<ProgramEventSlim[], string | undefined, ApiClientResponseError<DefaultResponseErrorDataObject>>({
    async queryFn({ pageParam: cursor }) {
      /* eslint-disable @typescript-eslint/naming-convention -- Start: Keys are defined by Backend API endpoint */
      const response = await getEvents<ProgramEventSlimDataObject[]>({
        config: {
          params: {
            cursor,
            featured: filter?.featured,
            limit: pagination.pageSize,
            organization_id: filter?.organizationId,
            scope: filter?.scope,
          },
        },
      });
      /* eslint-enable @typescript-eslint/naming-convention -- End: Keys are defined by Backend API endpoint */
      return response.data.map((event) => getProgramEventSlimData(event));
    },
    initialPageParam: undefined,
    getNextPageParam: (lastPage) => (lastPage.length < pagination.pageSize ? undefined : lastPage.at(-1)?.id),
    queryKey: getEventsInfiniteQueryKey({ filter, pagination }),
  });

// TODO: add params `scope` and `organization_id` once `useEventsInfiniteQuery` response types are unified
export const useEventsInfiniteQuery = ({ filter, options, pagination }: EventsQueryParams) => {
  const getEventsParameters = useGetEventsParameters();

  return useInfiniteQuery<
    ProgramListEvent[],
    string | undefined,
    ApiClientResponseError<DefaultResponseErrorDataObject>
  >({
    async queryFn({ pageParam: cursor }) {
      const response = await getEvents<ProgramListEventDataObject[]>({
        config: {
          params: getEventsParameters({ filter, pagination: { cursor, limit: pagination.pageSize } }),
        },
      });

      return response.data.map((event) => createProgramListEvent(event));
    },
    initialPageParam: undefined,
    getNextPageParam: (lastPage) => (lastPage.length < pagination.pageSize ? undefined : lastPage.at(-1)?.id),
    queryKey: getEventsInfiniteQueryKey({ filter, pagination }),
    // TODO this is set to `always` because events are not deleted from React Query, so there could be some bad data shown
    refetchOnMount: 'always',
    throwOnError: true,
    ...options,
  });
};
